import { useAtom, useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { useEffect, useMemo } from "react";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { yieldToMain } from "shared/lib/utils";

import { checkIsStyleArticle } from "scmp-app/components/article/article-render/style-article/helper";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { currentArticleAtom, currentShareContentAtom } from "scmp-app/lib/current-item";

import { contentShareWidgetDrawerAtom } from "./atoms";
import { StyledContentShareWidget, StyledDynamicDrawer } from "./styles";

export type Variant = "default" | "magazines-style";

export const ContentShareWidgetDrawer: FunctionComponent = () => {
  const currentArticle = useAtomValue(currentArticleAtom);
  const currentShareContent = useAtomValue(currentShareContentAtom);
  const [contentShareWidgetDrawerState, setContentShareWidgetDrawer] = useAtom(
    contentShareWidgetDrawerAtom,
  );

  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), true);
  useEffect(() => {
    setContentShareWidgetDrawer(props => ({ ...props, isOpen: false }));
  }, [isTabletUp, setContentShareWidgetDrawer]);

  useEffect(() => {
    if (!contentShareWidgetDrawerState.isOpen) return;

    void (async () => {
      await yieldToMain({ priority: "background" });
      sendGA4Tracking({
        action: "click",
        category: "share",
        customized_parameters: {
          action_type: "open_sharing_drawer",
          position: contentShareWidgetDrawerState.position,
        },
        subcategory: "",
      });
    })();
  }, [contentShareWidgetDrawerState.isOpen, contentShareWidgetDrawerState.position]);

  const variant: Variant = useMemo(() => {
    if (currentArticle && checkIsStyleArticle(currentArticle)) {
      return "magazines-style";
    }
    return "default";
  }, [currentArticle]);

  const shareContent = currentArticle ?? currentShareContent;
  if (!shareContent) return null;

  return (
    <StyledDynamicDrawer
      $variant={variant}
      anchor="bottom"
      onClose={() => setContentShareWidgetDrawer(props => ({ ...props, isOpen: false }))}
      open={contentShareWidgetDrawerState.isOpen}
      PaperProps={{
        sx: {
          alignItems: "center",
          border: "1px solid #000",
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
          display: "flex",
        },
      }}
      title="Share"
      withClose
    >
      <StyledContentShareWidget
        extraProps={{
          copyLink: {
            variant: "label",
          },
        }}
        onClick={() => {
          setTimeout(
            () => setContentShareWidgetDrawer(props => ({ ...props, isOpen: false })),
            500,
          );
        }}
        position={contentShareWidgetDrawerState.position ?? "article_adhesive"}
        reference={shareContent}
        showLabel
        types={["facebook", "twitter", "whatsapp", "telegram", "email", "copy-link"]}
      />
    </StyledDynamicDrawer>
  );
};

ContentShareWidgetDrawer.displayName = "ContentShareWidgetDrawer";
